import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { AdminPageHeader } from '../Common/AdminPagesHeader';
import { usePagination } from '../../../hooks';
import { popup, getAllUsers } from '../../../utils';

import { OptionIcon } from '../../../assets/svg/OptionIcon';
import ActivateUserModal from '../../shared/Modal/ActivateCSMModal';
import { Table } from '../../shared';
import { useSelector } from 'react-redux';
import { ROLES } from '../../../constants';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../../constants/queryKey';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const ManagementList = ({
    forRoles,
    heading,
    buttonText,
    searchBarPlaceholder,
    columns,
    stickyColumns,
    pageName,
    enableActiveUserButton,
    activateUserButtonTitle,
}) => {
    const [response, setResponse] = useState({ data: [], count: 0 });
    const pagination = usePagination(response.data.length, response.count);
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const role = useSelector((s) => s.user.role);

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const { data, isError, error, refetch, isFetching } = useQuery({
        queryKey: [
            QUERY_KEYS.USERS,
            pagination.limit,
            pagination.offset,
            debouncedSearch,
            forRoles,
        ],
        queryFn: () =>
            getAllUsers({
                limit: pagination.limit,
                offset: pagination.offset,
                search: debouncedSearch,
                filter: { status: true },
                role: forRoles,
            }),
        select: (data) => ({
            data: data?.data || [],
            count: data?.count || 0,
        }),
    });
    if (isError) popup('error', error?.message);

    useEffect(() => {
        if (data) {
            setResponse(data);
        }
    }, [data]);

    return (
        <>
            <AdminPageHeader
                heading={heading}
                buttonText={buttonText}
                searchBarPlaceholder={searchBarPlaceholder}
                setSearch={setSearch}
                count={pagination.dataCount}
                setCurrentPage={pagination.setCurrentPage}
                backbutton
            />
            <Table
                isLoading={isFetching}
                columns={columns}
                data={response.data}
                pagination={pagination}
                pageName={pageName}
                stickyColumns={stickyColumns}
                debouncedApiCall={refetch}
            />
            {showModal && [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(role) && (
                <ActivateUserModal
                    showModal={showModal}
                    setShowModal={setShowModal}
                    forRoles={forRoles}
                    searchBarPlaceholder={searchBarPlaceholder}
                    pageName={pageName}
                />
            )}
            {enableActiveUserButton && [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(role) && (
                <span
                    id='action-btn'
                    title={activateUserButtonTitle}
                    aria-label={activateUserButtonTitle}
                    onClick={() => setShowModal(true)}
                    className={`unhide-hc-btn ${response?.data?.length === 0 ? 'no-data' : ''}`}
                >
                    <OptionIcon />
                </span>
            )}
        </>
    );
};

ManagementList.defaultProps = {
    forRoles: [],
    status: true,
    heading: '',
    buttonText: '',
    searchBarPlaceholder: 'Search...',
    columns: [{}, {}],
    stickyColumns: [],
    pageName: '',
    enableActiveUserButton: false,
    activateUserButtonTitle: '',
};

ManagementList.propTypes = {
    forRoles: PropTypes.array.isRequired,
    status: PropTypes.bool,
    heading: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    searchBarPlaceholder: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    stickyColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
    pageName: PropTypes.string.isRequired,
    enableActiveUserButton: PropTypes.bool,
    activateUserButtonTitle: PropTypes.string,
};
